import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Pill as PillIcon, Plus, Eye, Package, X, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useArticleUnits } from '../hooks/useArticleUnits';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function userCan(user, mod, action) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.permissions?.[mod]?.[action] === true;
}

function InternalConsumptions() {
  const { user } = useOutletContext();
  const { unitOf } = useArticleUnits();
  const navigate = useNavigate();
  const [consumptions, setConsumptions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [areaStock, setAreaStock] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ area_id: '', recipient: '', reason: '', notes: '', items: [] });
  const [newItem, setNewItem] = useState({ source_article_id: '', article_name: '', quantity: '' });
  const [articleSearch, setArticleSearch] = useState('');
  const [detail, setDetail] = useState(null);

  const canConsume = userCan(user, 'pedidos_internos', 'consumir');
  const canDelete = (c) => user?.role === 'admin' || user?.role === 'supervisor' || c?.created_by === user?.user_id;
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    // v3.30.15 · Cargamos consumos y áreas por separado para que el error de
    // uno no reviente al otro (antes el `Promise.all` cortaba el pipeline si
    // consumos tiraba 403, dejando el select de áreas vacío).
    try {
      const c = await axios.get(`${BACKEND_URL}/api/internal-consumptions`, { withCredentials: true });
      setConsumptions(c.data || []);
    } catch (e) {
      setConsumptions([]);
      toast.error(e.response?.data?.detail || 'Error al cargar consumos');
    }
    try {
      const a = await axios.get(`${BACKEND_URL}/api/me/areas`, { withCredentials: true });
      const myAreas = (a.data || []).filter(x => x.is_active !== false);
      setAreas(myAreas);
    } catch (e) {
      setAreas([]);
      toast.error(e.response?.data?.detail || 'Error al cargar áreas');
    }
    setLoading(false);
  };

  const fetchAreaStock = async (areaId) => {
    if (!areaId) { setAreaStock(null); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/internal-areas/${areaId}/stock`, { withCredentials: true });
      setAreaStock(res.data);
    } catch (e) {
      setAreaStock(null);
      toast.error(e.response?.data?.detail || 'No se pudo cargar el stock del área');
    }
  };

  const openNew = () => {
    setForm({ area_id: '', recipient: '', reason: '', notes: '', items: [] });
    setNewItem({ source_article_id: '', article_name: '', quantity: '' });
    setArticleSearch('');
    setAreaStock(null);
    setDialogOpen(true);
  };

  const onAreaChange = (areaId) => {
    setForm({ ...form, area_id: areaId, items: [] });
    fetchAreaStock(areaId);
  };

  const addItem = () => {
    const qty = parseInt(newItem.quantity);
    if (!newItem.source_article_id || !qty || qty < 1) {
      toast.error('Selecciona artículo y cantidad');
      return;
    }
    if (form.items.some(i => i.source_article_id === newItem.source_article_id)) {
      toast.error('Ese artículo ya está agregado');
      return;
    }
    const found = areaStock?.items?.find(s => s.article_id === newItem.source_article_id);
    if (found && qty > found.total_stock) {
      toast.error(`Stock insuficiente: hay ${found.total_stock}u`);
      return;
    }
    setForm({
      ...form,
      items: [...form.items, {
        source_article_id: newItem.source_article_id,
        article_name: found?.article_name || '',
        is_medicamento: !!found?.is_medicamento,
        quantity: qty,
      }],
    });
    setNewItem({ source_article_id: '', article_name: '', quantity: '' });
  };

  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.area_id || !form.recipient.trim() || form.items.length === 0) {
      toast.error('Completá área, destinatario y al menos un artículo');
      return;
    }
    try {
      // El backend espera article_id "fuente". El stock del área devuelve article_id ya con sufijo "__d_<wh>",
      // pero contiene source_article_id implícito en el grupo. Mandamos directamente el article_id (mirror).
      // Para evitar confusión: el endpoint POST acepta el id "espejo" o el original.
      // Acá usamos el article_id que viene del area-stock (que es el espejo del depósito destino).
      const payload = {
        area_id: form.area_id,
        recipient: form.recipient.trim(),
        reason: form.reason,
        notes: form.notes,
        items: form.items.map(i => ({ article_id: i.source_article_id, quantity: i.quantity })),
      };
      await axios.post(`${BACKEND_URL}/api/internal-consumptions`, payload, { withCredentials: true });
      toast.success('Consumo registrado');
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const openPdf = async (c) => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/internal-consumptions/${c.consumption_id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error('No se pudo generar el PDF');
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/internal-consumptions/${deleteTarget.consumption_id}`, { withCredentials: true });
      toast.success(`Consumo anulado. Se revirtieron ${res.data?.reverted_qty ?? 0}u al stock del área.`);
      setDeleteTarget(null);
      if (form.area_id) fetchAreaStock(form.area_id);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo anular el consumo');
    } finally {
      setDeleting(false);
    }
  };

  const stockOptions = (areaStock?.items || [])
    .filter(it => it.total_stock > 0)
    .sort((a, b) => (a.article_name || '').localeCompare(b.article_name || '', 'es', { sensitivity: 'base' }));
  const normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filteredStockOptions = stockOptions.filter(it => {
    if (!articleSearch.trim()) return true;
    return normalize(it.article_name).includes(normalize(articleSearch));
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <PillIcon className="w-7 h-7 text-rose-500" /> Consumo Interno
        </h1>
        {canConsume && (
          <Button onClick={openNew} data-testid="new-consumption-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Consumo
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="w-4 h-4" /> Stock del Área
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-3">
            <Label className="whitespace-nowrap">Área:</Label>
            <select
              value={form.area_id || ''}
              onChange={(e) => onAreaChange(e.target.value)}
              className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Selecciona un área para ver su stock...</option>
              {areas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
            </select>
            {areaStock?.area && (
              <Button variant="outline" size="sm" onClick={() => navigate('/internal-requests')}>
                Ver pedidos del área
              </Button>
            )}
          </div>
          {areaStock && (
            <>
              {areaStock.pending_receipt_lots_count > 0 && (
                <div className="mb-3 p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-sm flex items-center gap-2" data-testid="pending-receipt-banner">
                  <span className="text-amber-700 dark:text-amber-400">⏳</span>
                  <span className="text-amber-900 dark:text-amber-200">
                    Hay {areaStock.pending_receipt_lots_count} lote(s) pendientes de confirmar recepción. No están disponibles hasta confirmar la OPI correspondiente.
                  </span>
                </div>
              )}
              {stockOptions.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sin stock en el depósito del área. Generá una OPI para abastecer.</p>
              ) : (
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artículo</TableHead>
                    <TableHead className="text-center">Stock Total</TableHead>
                    <TableHead>Lotes (FEFO)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockOptions.map(it => (
                    <TableRow key={it.article_id} className={it.low_stock ? 'bg-amber-50' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {it.is_medicamento && '💊'} {it.article_name}
                          {it.low_stock && <Badge className="bg-amber-200 text-amber-900 text-xs">Stock bajo</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-bold">{it.total_stock} <span className="text-xs font-normal text-muted-foreground">{unitOf(it.article_id)}</span></TableCell>
                      <TableCell className="text-xs">
                        {it.lots.map((l, i) => {
                          const expCls = l.days_to_expire !== null && l.days_to_expire <= 30 ? 'text-red-600 font-medium' :
                                         l.days_to_expire !== null && l.days_to_expire <= 90 ? 'text-amber-700' : '';
                          return (
                            <div key={i}>
                              Lote <b>{l.lot_number || 's/lote'}</b>{' '}
                              {l.expiration_date && <span className={expCls}>(vence {l.expiration_date}{l.days_to_expire !== null ? `, ${l.days_to_expire}d` : ''})</span>}{' '}
                              × {l.quantity_remaining}
                            </div>
                          );
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Consumos registrados</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p>Cargando...</p> : consumptions.length === 0 ? <p className="text-muted-foreground">Sin consumos registrados.</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Comprobante</TableHead>
                  <TableHead>Área</TableHead>
                  <TableHead>Destinatario</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consumptions.map(c => (
                  <TableRow key={c.consumption_id}>
                    <TableCell className="font-mono text-xs">{c.voucher_number}</TableCell>
                    <TableCell>{c.area_name}</TableCell>
                    <TableCell className="text-sm">{c.recipient}</TableCell>
                    <TableCell>{c.items?.length || 0}</TableCell>
                    <TableCell className="text-sm">{c.created_by_name}</TableCell>
                    <TableCell className="text-xs">{c.created_at && fmtDateTime(c.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setDetail(c)} data-testid={`view-consumption-${c.consumption_id}`} title="Ver detalle">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openPdf(c)} data-testid={`pdf-consumption-${c.consumption_id}`} title="Comprobante PDF">
                          <FileText className="w-4 h-4 text-blue-600" />
                        </Button>
                        {canDelete(c) && (
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(c)} data-testid={`delete-consumption-${c.consumption_id}`} title="Anular (revierte stock)">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Crear consumo */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Consumo Interno</DialogTitle>
            <DialogDescription>Salida del stock del área hacia un destinatario (residente, pastillero, sala, etc.).</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Área *</Label>
              <select
                value={form.area_id}
                onChange={(e) => onAreaChange(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                required
                data-testid="ci-area"
              >
                <option value="">Selecciona...</option>
                {areas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Destinatario * (residente, pastillero, sala, etc.)</Label>
              <Input
                value={form.recipient}
                onChange={(e) => setForm({ ...form, recipient: e.target.value })}
                placeholder="Ej: Residente Juan Pérez - Habitación 204"
                required
                data-testid="ci-recipient"
              />
            </div>
            <div>
              <Label>Motivo (opcional)</Label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Ej: Tratamiento post-quirúrgico" />
            </div>

            {form.area_id && (
              <div className="border rounded-md p-3 space-y-2">
                <Label className="text-base">Agregar artículo</Label>
                <Input
                  type="text"
                  value={articleSearch}
                  onChange={(e) => setArticleSearch(e.target.value)}
                  placeholder="Buscar artículo por nombre..."
                  data-testid="ci-article-search"
                />
                <div className="flex gap-2 items-start">
                  <select
                    value={newItem.source_article_id}
                    onChange={(e) => setNewItem({ ...newItem, source_article_id: e.target.value })}
                    className="flex flex-1 min-w-0 rounded-md border border-input bg-background px-2 py-1 text-sm"
                    size={Math.min(8, Math.max(4, filteredStockOptions.length || 1))}
                    data-testid="ci-article"
                  >
                    {filteredStockOptions.length === 0 ? (
                      <option value="" disabled>Sin resultados</option>
                    ) : (
                      filteredStockOptions.map(it => (
                        <option key={it.article_id} value={it.article_id}>
                          {it.is_medicamento ? '💊 ' : ''}{it.article_name} — disp.: {it.total_stock}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="flex flex-col gap-2 w-24">
                    <Input
                      type="number"
                      min="1"
                      placeholder="Cant."
                      value={newItem.quantity}
                      onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                      data-testid="ci-qty"
                    />
                    <Button type="button" onClick={addItem}><Plus className="w-4 h-4" /></Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  {filteredStockOptions.length} de {stockOptions.length} artículos
                </p>
              </div>
            )}

            {form.items.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Artículo</TableHead><TableHead>Cant.</TableHead><TableHead></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {form.items.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{it.is_medicamento && '💊 '}{it.article_name}</TableCell>
                      <TableCell>{it.quantity} <span className="text-xs text-muted-foreground">{unitOf(it.source_article_id)}</span></TableCell>
                      <TableCell>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <div>
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={form.items.length === 0} data-testid="ci-submit">Registrar Consumo</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detalle */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>{detail.voucher_number}</DialogTitle>
                <DialogDescription>{detail.area_name} → {detail.recipient}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {detail.reason && <p><b>Motivo:</b> {detail.reason}</p>}
                {detail.notes && <p><b>Notas:</b> {detail.notes}</p>}
                <p className="text-xs text-muted-foreground">{detail.created_by_name} · {detail.created_at && fmtDateTime(detail.created_at)}</p>
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Artículo</TableHead><TableHead className="text-center">Cant.</TableHead><TableHead>Lotes</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.items.map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.article_name}</TableCell>
                        <TableCell className="text-center">{it.quantity} <span className="text-xs text-muted-foreground">{unitOf(it.article_id)}</span></TableCell>
                        <TableCell className="text-xs">
                          {(it.allocations || []).map((a, j) => (
                            <div key={j}>Lote <b>{a.lot_number || 's/lote'}</b> × {a.qty}</div>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => openPdf(detail)} data-testid="detail-pdf-btn">
                  <FileText className="w-4 h-4 mr-2" /> Comprobante PDF
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title={`¿Anular el consumo ${deleteTarget?.voucher_number || ''}?`}
        description={<>Esta acción <strong>revierte el stock</strong>: devuelve las cantidades a los mismos lotes del área y elimina los movimientos de egreso asociados. Esta operación no se puede deshacer.</>}
        confirmText="Anular y revertir stock"
        loading={deleting}
        onConfirm={doDelete}
        testId="confirm-delete-consumption"
      />
    </div>
  );
}

export default InternalConsumptions;
